import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FeatherDownload, FeatherFilePlus, FeatherX } from "@subframe/core";
import { Button } from "../../../../ui/components/Button";
import { IconButton } from "../../../../ui/components/IconButton";
import { Loader } from "../../../../ui/components/Loader";
import { TextField } from "../../../../ui/components/TextField";
import { DialogLayout } from "../../../../ui/layouts/DialogLayout";
import { DateRangeFilter } from "../../../../shared/presentation/components/DateRangeFilter";
import {
  DateRangeValue,
  formatDateForApi,
} from "../../../../shared/presentation/utils/dateRangeFilters";
import { usePersistedDateRange } from "../../../../shared/presentation/hooks/usePersistedDateRange";
import { useMinimumLoading } from "../../../../shared/presentation/hooks/useMinimumLoading";
import { usePersistedSort } from "../../../../shared/presentation/hooks/usePersistedSort";
import { usePersistedState } from "../../../../shared/presentation/hooks/usePersistedState";
import { usePermissions } from "../../../../shared/presentation/hooks";
import { useToast } from "../../../../shared/presentation/components";
import { ACTION_CODES, MODULE_CODES } from "../../../../shared/constants/permissions";
import {
  EXPORT_MAX_DATE_RANGE_INCLUSIVE_DAYS,
  addDaysLocal,
  parseYmdToLocalDate,
  toYmd,
  validateExportDateRange,
} from "../../../../shared/utils/date";
import { JournalEntity, JournalListStatus } from "../../domain";
import {
  useDuplicateJournal,
  useJournalCounts,
  useJournalExcelExport,
  useJournalList,
} from "../hooks";
import { JournalListToolbar } from "./JournalListToolbar";
import { JournalStatsCards } from "./JournalStatsCards";
import { JournalTable } from "./JournalTable";

const ITEMS_PER_PAGE = 10;
const JOURNAL_LIST_STATUSES: JournalListStatus[] = [
  "all",
  "under_review",
  "approved",
  "rejected",
  "save_as_draft",
  "ap_ar",
];

type ListLoadingReason = "initial" | "pagination" | "refresh" | null;

const deserializeJournalStatus = (
  raw: string,
  fallback: JournalListStatus,
) => {
  const parsed = JSON.parse(raw);
  return typeof parsed === "string" &&
    JOURNAL_LIST_STATUSES.includes(parsed as JournalListStatus)
    ? (parsed as JournalListStatus)
    : fallback;
};

const JournalSection: React.FC = () => {
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const { hasPermission } = usePermissions();
  const canDuplicateJournal = hasPermission(
    MODULE_CODES.JOURNAL,
    ACTION_CODES.DUPLICATE_JOURNAL,
  );

  const [status, setStatus] = usePersistedState<JournalListStatus>(
    "journals.list.status",
    "all",
    { deserialize: deserializeJournalStatus },
  );
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [dateRange, setDateRange] = usePersistedDateRange(
    "journals.list.dateRange",
  );
  const [sort, setSort] = usePersistedSort("journals.list", {
    by: "created_at",
    order: "desc",
    label: "Created Date: Newest First",
  });
  const [listLoadingReason, setListLoadingReason] =
    useState<ListLoadingReason>("initial");
  const listLoadingStartedRef = useRef(false);

  const { journals, pagination, loading, error, fetchJournals } =
    useJournalList();
  const { duplicate, duplicatingJournalId } = useDuplicateJournal();
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [exportFromDate, setExportFromDate] = useState("");
  const [exportToDate, setExportToDate] = useState("");
  const [exportError, setExportError] = useState("");
  const { download: downloadExport, loading: isExporting } =
    useJournalExcelExport();

  const countParams = useMemo(
    () => ({
      date_from:
        dateRange.preset === "all_time"
          ? undefined
          : formatDateForApi(dateRange.from),
      date_to:
        dateRange.preset === "all_time"
          ? undefined
          : formatDateForApi(dateRange.to),
    }),
    [dateRange.from, dateRange.preset, dateRange.to],
  );

  const {
    counts,
    loading: countsLoading,
    fetchCounts,
  } = useJournalCounts(countParams);

  const listParams = useMemo(
    () => ({
      page,
      limit: ITEMS_PER_PAGE,
      status,
      search: searchTerm,
      dateFrom:
        dateRange.preset === "all_time"
          ? undefined
          : formatDateForApi(dateRange.from),
      dateTo:
        dateRange.preset === "all_time"
          ? undefined
          : formatDateForApi(dateRange.to),
      sortBy: sort.by as
        | "created_at"
        | "updated_at"
        | "date"
        | "approved_at"
        | "journal_number",
      sortOrder: sort.order as "asc" | "desc",
    }),
    [
      dateRange.from,
      dateRange.preset,
      dateRange.to,
      page,
      searchTerm,
      sort.by,
      sort.order,
      status,
    ],
  );

  useEffect(() => {
    const timeoutId = window.setTimeout(
      () => {
        fetchJournals(listParams);
      },
      searchTerm.trim() ? 500 : 0,
    );

    return () => window.clearTimeout(timeoutId);
  }, [fetchJournals, listParams, searchTerm]);

  useEffect(() => {
    if (loading) {
      listLoadingStartedRef.current = true;
      return;
    }

    if (listLoadingStartedRef.current) {
      listLoadingStartedRef.current = false;
      setListLoadingReason(null);
    }
  }, [loading]);

  const handleStatusChange = (nextStatus: JournalListStatus) => {
    if (nextStatus === status) return;
    setListLoadingReason("refresh");
    setStatus(nextStatus);
    setPage(1);
  };

  const handleDateRangeChange = (nextDateRange: DateRangeValue) => {
    if (
      nextDateRange.preset === dateRange.preset &&
      nextDateRange.from?.getTime() === dateRange.from?.getTime() &&
      nextDateRange.to?.getTime() === dateRange.to?.getTime()
    ) {
      return;
    }

    setListLoadingReason("refresh");
    setDateRange(nextDateRange);
    setPage(1);
  };

  const handleSearchTermChange = (value: string) => {
    if (value === searchTerm) return;
    setListLoadingReason("refresh");
    setSearchTerm(value);
    setPage(1);
  };

  const handleSortChange = (
    sortBy: string,
    sortOrder: string,
    label: string,
  ) => {
    if (sortBy === sort.by && sortOrder === sort.order && label === sort.label) {
      return;
    }

    setListLoadingReason("refresh");
    setSort({ by: sortBy, order: sortOrder, label });
    setPage(1);
  };

  const handleRefresh = useCallback(() => {
    setListLoadingReason("refresh");
    fetchJournals(listParams);
    fetchCounts(countParams);
  }, [countParams, fetchCounts, fetchJournals, listParams]);

  const handlePageChange = (nextPage: number) => {
    if (nextPage === page) return;
    setListLoadingReason("pagination");
    setPage(nextPage);
  };

  const handleViewJournal = (journal: JournalEntity) => {
    navigate(`/journal/detail/${journal.id}`, {
      state: {
        journalType: journal.journal_type,
      },
    });
  };

  const handleDuplicateJournal = async (journal: JournalEntity) => {
    try {
      const result = await duplicate(journal.id);
      showSuccess(result.message || "Journal duplicated successfully");
      if (result.newJournalId) {
        navigate(`/journal/edit/${result.newJournalId}`);
      }
    } catch (error: any) {
      showError(error.message || "Failed to duplicate journal. Please try again.");
    }
  };

  const resetExportState = () => {
    setExportFromDate("");
    setExportToDate("");
    setExportError("");
  };

  const exportDateRangeError = validateExportDateRange(
    exportFromDate,
    exportToDate,
  );

  const handleExportFromDateChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = event.target.value;
    setExportFromDate(value);
    setExportError("");
    if (!value) return;

    const fromDate = parseYmdToLocalDate(value);
    if (!fromDate || !exportToDate) return;

    const toDate = parseYmdToLocalDate(exportToDate);
    const maxToDate = addDaysLocal(
      fromDate,
      EXPORT_MAX_DATE_RANGE_INCLUSIVE_DAYS - 1,
    );

    if (toDate && toDate > maxToDate) {
      setExportToDate(toYmd(maxToDate));
    } else if (toDate && toDate < fromDate) {
      setExportToDate(value);
    }
  };

  const handleExportToDateChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = event.target.value;
    setExportToDate(value);
    setExportError("");
    if (!value) return;

    const toDate = parseYmdToLocalDate(value);
    if (!toDate || !exportFromDate) return;

    const fromDate = parseYmdToLocalDate(exportFromDate);
    const minFromDate = addDaysLocal(
      toDate,
      -(EXPORT_MAX_DATE_RANGE_INCLUSIVE_DAYS - 1),
    );

    if (fromDate && fromDate < minFromDate) {
      setExportFromDate(toYmd(minFromDate));
    } else if (fromDate && fromDate > toDate) {
      setExportFromDate(value);
    }
  };

  const exportFromMin =
    exportToDate.trim() && parseYmdToLocalDate(exportToDate.trim())
      ? toYmd(
          addDaysLocal(
            parseYmdToLocalDate(exportToDate.trim())!,
            -(EXPORT_MAX_DATE_RANGE_INCLUSIVE_DAYS - 1),
          ),
        )
      : undefined;
  const exportFromMax =
    exportToDate.trim() && parseYmdToLocalDate(exportToDate.trim())
      ? exportToDate.trim()
      : undefined;
  const exportToMin =
    exportFromDate.trim() && parseYmdToLocalDate(exportFromDate.trim())
      ? exportFromDate.trim()
      : undefined;
  const exportToMax =
    exportFromDate.trim() && parseYmdToLocalDate(exportFromDate.trim())
      ? toYmd(
          addDaysLocal(
            parseYmdToLocalDate(exportFromDate.trim())!,
            EXPORT_MAX_DATE_RANGE_INCLUSIVE_DAYS - 1,
          ),
        )
      : undefined;

  const handleExportDownload = async () => {
    setExportError("");
    const rangeError = validateExportDateRange(exportFromDate, exportToDate);
    if (rangeError) {
      setExportError(rangeError);
      return;
    }

    try {
      await downloadExport({
        fromDate: exportFromDate.trim(),
        toDate: exportToDate.trim(),
      });
      setIsExportModalOpen(false);
      resetExportState();
    } catch (error: any) {
      setExportError(error.message || "Failed to download journal export.");
    }
  };

  const showTableSkeleton =
    (listLoadingReason === "initial" && !pagination) ||
    listLoadingReason === "pagination" ||
    (loading && !pagination);
  const showInlineLoading =
    !showTableSkeleton && (listLoadingReason === "refresh" || loading);
  const smoothTableSkeleton = useMinimumLoading(showTableSkeleton);
  const smoothInlineLoading = useMinimumLoading(showInlineLoading);

  return (
    <div className="flex w-full grow shrink-0 basis-0 flex-col items-start gap-8 bg-default-background px-8 pt-8 pb-40">
      <div className="flex w-full items-center justify-between">
        <span className="text-heading-2 font-heading-2 text-default-font">
          Journals Entries
        </span>
        <div className="flex items-center gap-2">
          <Button
            variant="neutral-tertiary"
            icon={<FeatherDownload />}
            onClick={() => setIsExportModalOpen(true)}
          >
            Export
          </Button>
          <Button
            icon={<FeatherFilePlus />}
            onClick={() => navigate("/journal/new")}
          >
            New Journal Entry
          </Button>
        </div>
      </div>

      <DateRangeFilter value={dateRange} onChange={handleDateRangeChange} />

      <JournalStatsCards
        counts={counts}
        loading={countsLoading}
        onSelectStatus={handleStatusChange}
        onManageRecurring={() => navigate("/journal/recurring")}
      />

      <JournalListToolbar
        status={status}
        onStatusChange={handleStatusChange}
        searchTerm={searchTerm}
        onSearchTermChange={handleSearchTermChange}
        sort={sort}
        onSortChange={handleSortChange}
        onRefresh={handleRefresh}
        loading={loading}
      />

      <JournalTable
        journals={journals}
        loading={smoothTableSkeleton}
        inlineLoading={smoothInlineLoading}
        error={error}
        pagination={pagination}
        itemsPerPage={ITEMS_PER_PAGE}
        searchTerm={searchTerm}
        canDuplicateJournal={canDuplicateJournal}
        duplicatingJournalId={duplicatingJournalId}
        onView={handleViewJournal}
        onDuplicate={handleDuplicateJournal}
        onPageChange={handlePageChange}
      />

      <DialogLayout
        open={isExportModalOpen}
        onOpenChange={(open) => {
          setIsExportModalOpen(open);
          if (!open) resetExportState();
        }}
      >
        <div className="flex w-144 flex-col items-start gap-6 bg-white px-8 py-8">
          <div className="flex w-full items-center justify-between">
            <span className="text-heading-2 font-heading-2 text-default-font">
              Export journals to Excel
            </span>
            <IconButton
              icon={<FeatherX />}
              onClick={() => setIsExportModalOpen(false)}
              disabled={isExporting}
            />
          </div>

          <div className="flex w-full flex-col items-start gap-4">
            {exportError && (
              <div className="w-full rounded-md border border-error-200 bg-error-50 px-4 py-3">
                <span className="text-body font-body text-error-700">
                  {exportError}
                </span>
              </div>
            )}

            <div className="flex w-full items-start gap-4">
              <TextField
                className="h-auto w-full"
                variant="filled"
                label="From date"
                helpText="Required"
              >
                <TextField.Input
                  type="date"
                  required
                  value={exportFromDate}
                  onChange={handleExportFromDateChange}
                  disabled={isExporting}
                  min={exportFromMin}
                  max={exportFromMax}
                />
              </TextField>
              <TextField
                className="h-auto w-full"
                variant="filled"
                label="To date"
                helpText="Required"
              >
                <TextField.Input
                  type="date"
                  required
                  value={exportToDate}
                  onChange={handleExportToDateChange}
                  disabled={isExporting}
                  min={exportToMin}
                  max={exportToMax}
                />
              </TextField>
            </div>

            <div className="w-full rounded-md border border-solid border-neutral-border bg-neutral-50 px-4 py-3">
              <span className="text-body font-body text-subtext-color">
                Filter applies to journal date. Select a range of up to{" "}
                {EXPORT_MAX_DATE_RANGE_INCLUSIVE_DAYS} days (inclusive). Both
                dates are required.
              </span>
            </div>
          </div>

          <div className="flex w-full items-center justify-end gap-2">
            <Button
              variant="neutral-tertiary"
              onClick={() => setIsExportModalOpen(false)}
              disabled={isExporting}
            >
              Cancel
            </Button>
            <Button
              onClick={handleExportDownload}
              disabled={isExporting || !!exportDateRangeError}
            >
              {isExporting ? (
                <div className="flex items-center gap-2">
                  <Loader size="small" />
                  <span>Generating...</span>
                </div>
              ) : (
                "Download"
              )}
            </Button>
          </div>
        </div>
      </DialogLayout>
    </div>
  );
};

export default JournalSection;
